import { Button, Col, Form, Modal, Row, Select, Table } from "antd";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import { useState, useEffect, useMemo } from "react";
import { CloseOutlined, PlusOutlined, UnorderedListOutlined } from "@ant-design/icons";
import useGet from "../../../global/hooks/useGet";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import dayjs from "dayjs";
import usePost from "../../../global/hooks/usePost";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { useNavigate } from "react-router-dom";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { formatYearMonthToLabel } from "../../../global/utils/unixToLocalTime";
import "../Page/Invoice.css";

const initialFilterState = {
  OemNo: "",
  DealerNo: "",
  Month: dayjs().format("YYYY-MM"),
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return `$ ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const quantityLabel = (order) => {
  if (order?.QuantityLabel) return order.QuantityLabel;
  if (order?.Quantity != null) {
    if (order.Update && order.OldQuantity != null) {
      return `${order.OldQuantity} → ${order.Quantity}`;
    }
    return String(order.Quantity);
  }
  return "—";
};

const remainingKindLabel = (dealer) => {
  if (dealer?.Kind === "update") return "Update";
  if (dealer?.Kind === "remaining") return "Remaining";
  if (dealer?.Kind === "mixed") return "Remaining + update";
  return "Still open";
};

const orderKindLabel = (order) => {
  if (order?.Update || order?.Kind === "update") return "Update";
  if (order?.Kind === "remaining") return "Remaining";
  return "";
};

const periodLabel = (record) => {
  if (record?.Period) {
    return `${record.Period}${record.Days ? ` · ${record.Days}d` : ""}`;
  }
  if (record?.Prior) {
    return `Previous period${record.Age ? ` · ${record.Age}` : ""}`;
  }
  return "This month";
};

const InvoiceCreateForm = () => {
  const navigate = useNavigate();
  const logData = JSON.parse(sessionStorage.getItem("userData"));
  const [filter, setFilter] = useState(initialFilterState);
  const [form] = Form.useForm();
  const role_value = logData?.role_value;
  const [selectedDealerNos, setSelectedDealerNos] = useState([]);
  const [previewParams, setPreviewParams] = useState({
    Month: dayjs().format("YYYY-MM"),
  });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [checkedItemNos, setCheckedItemNos] = useState([]);
  const [expandedDealerNos, setExpandedDealerNos] = useState([]);
  const [resultBanner, setResultBanner] = useState(null);
  const [processView, setProcessView] = useState("open");

  const postUrl = `${process.env.REACT_APP_API_URL}/api/invoices/create-invoice/`;
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  const previewQuery = new URLSearchParams();
  if (previewParams.Month) previewQuery.set("Month", previewParams.Month);
  if (previewParams.OemNo) previewQuery.set("OemNo", previewParams.OemNo);
  if (previewParams.DealerNo) previewQuery.set("DealerNo", previewParams.DealerNo);
  const previewUrl = previewParams.Month
    ? `${process.env.REACT_APP_API_URL}/api/invoices/generate-preview/?${previewQuery}`
    : null;

  const {
    data: previewData,
    loading: previewLoading,
    refetch: previewRefetch,
  } = useGet(previewUrl);

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
  const { data: optionData } = useGet(optionUrl);
  const dealerOptionsUrl = filter?.OemNo
    ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${filter.OemNo}/dealers/`
    : ![1, 2, 3].includes(role_value) && logData?.oem_no
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${logData.oem_no}/dealers/`
      : null;
  const { data: dealerOptions } = useGet(dealerOptionsUrl);

  const [modifiedOptions, setModifiedOptions] = useState({
    oems: [],
    dealers: [],
  });

  const originalOptions = useMemo(
    () => ({
      oems: getModifiedResponse(optionData, "oems") || [],
      dealers: dealerOptions
        ? getModifiedResponse(dealerOptions, null) || []
        : getModifiedResponse(optionData, "dealers") || [],
    }),
    [optionData, dealerOptions],
  );

  useEffect(() => {
    setModifiedOptions(originalOptions);
  }, [originalOptions]);

  const flaggedDealers = previewData?.data?.Flagged || [];
  const openDealers = previewData?.data?.Open || [];
  const previewSummary = previewData?.data?.Summary || {};

  const dealerMap = useMemo(() => {
    const map = new Map();
    [...flaggedDealers, ...openDealers].forEach((dealer) => {
      map.set(dealer.DealerNo, dealer);
    });
    return map;
  }, [flaggedDealers, openDealers]);

  const selectedDealers = selectedDealerNos
    .map((id) => dealerMap.get(id))
    .filter(Boolean);

  const flaggedIds = flaggedDealers.map((dealer) => dealer.DealerNo);
  const openIds = openDealers.map((dealer) => dealer.DealerNo);
  const selectedOpenCount = selectedDealerNos.filter((id) =>
    openIds.includes(id),
  ).length;
  const selectedUpdateCount = selectedDealerNos.filter((id) =>
    flaggedIds.includes(id),
  ).length;
  const generateStep = reviewOpen ? 3 : selectedDealerNos.length ? 2 : 1;
  const monthLabel = filter.Month
    ? formatYearMonthToLabel(filter.Month)
    : "this month";

  const toggleExpand = (dealerNo, event) => {
    event?.stopPropagation?.();
    setExpandedDealerNos((prev) =>
      prev.includes(dealerNo)
        ? prev.filter((id) => id !== dealerNo)
        : [...prev, dealerNo],
    );
  };

  const handleReset = () => {
    const initialMonth = dayjs();
    form.resetFields();
    form.setFieldsValue({ Month: initialMonth });
    setFilter({ ...initialFilterState, Month: initialMonth.format("YYYY-MM") });
    setPreviewParams({ Month: initialMonth.format("YYYY-MM") });
    setSelectedDealerNos([]);
    setExpandedDealerNos([]);
    setReviewOpen(false);
    setResultBanner(null);
    setProcessView("open");
  };

  useEffect(() => {
    const previousMonth = dayjs(filter.Month, "YYYY-MM", true).isValid()
      ? dayjs(filter.Month, "YYYY-MM")
      : dayjs();
    setFilter((prev) => ({ ...prev, Month: previousMonth.format("YYYY-MM") }));
    form.setFieldsValue({ Month: previousMonth });
    setPreviewParams({ Month: previousMonth.format("YYYY-MM") });
  }, []);

  const applyPreview = (nextFilter) => {
    if (!nextFilter.Month) {
      form.setFields([
        {
          name: "Month",
          errors: ["Month is required"],
        },
      ]);
      return;
    }
    const nextParams = { Month: nextFilter.Month };
    if (nextFilter.OemNo) nextParams.OemNo = nextFilter.OemNo;
    if (nextFilter.DealerNo) nextParams.DealerNo = nextFilter.DealerNo;
    setPreviewParams(nextParams);
    setSelectedDealerNos([]);
    setExpandedDealerNos([]);
    setResultBanner(null);
  };

  const handleFilter = () => {
    applyPreview(filter);
  };

  const openReview = () => {
    if (!selectedDealers.length) return;
    const nextChecked = selectedDealers.flatMap((dealer) =>
      (dealer.Orders || [])
        .map((order) => Number(order.ItemNo))
        .filter((id) => Number.isInteger(id) && id > 0),
    );
    setCheckedItemNos([...new Set(nextChecked)]);
    setReviewOpen(true);
  };

  const reviewTableRows = selectedDealers.flatMap((dealer) =>
    (dealer.Orders || []).map((order) => ({
      ...order,
      rowKey: `${dealer.DealerNo}-${order.ItemNo}`,
      DealerName: dealer.DealerName,
    })),
  );
  const reviewOrders = reviewTableRows.filter((order) =>
    checkedItemNos.includes(Number(order.ItemNo)),
  );
  const reviewAmount = reviewOrders.reduce(
    (sum, order) => sum + Number(order.Amount || 0),
    0,
  );
  const reviewColumns = [
    {
      title: "Dealer",
      dataIndex: "DealerName",
      width: 180,
      ellipsis: true,
      render: (value) => value || "N/A",
    },
    {
      title: "Order",
      dataIndex: "OrderNo",
      width: 90,
      ellipsis: true,
      render: (value) => value || "—",
    },
    {
      title: "Item",
      dataIndex: "Item",
      width: 200,
      ellipsis: true,
      render: (value, record) => {
        const kind = orderKindLabel(record);
        return (
          <span className="invoice-dealer-cell">
            <span>{value || "—"}</span>
            {kind ? (
              <span
                className={`invoice-process-pill ${
                  kind === "Update" ? "is-update" : "is-remaining"
                }`}
              >
                {kind}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      title: "Quantity",
      width: 90,
      ellipsis: true,
      render: (_, record) => quantityLabel(record),
    },
    {
      title: "Device",
      dataIndex: "Mac",
      width: 200,
      ellipsis: true,
      render: (value, record) =>
        `${value || "—"}${record.LicenseType ? ` · ${record.LicenseType}` : ""}`,
    },
    {
      title: "Period",
      width: 200,
      ellipsis: true,
      render: (_, record) => periodLabel(record),
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      width: 120,
      ellipsis: true,
      render: (value) => formatMoney(value),
    },
  ];

  const confirmGenerate = () => {
    const itemIds = [
      ...new Set(
        checkedItemNos
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0),
      ),
    ];
    const data = {
      month: previewParams.Month || filter.Month,
      dealer_ids: selectedDealerNos,
      item_ids: itemIds,
    };
    postAction(data);
  };

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      const skipped = postData?.skipped_dealers?.length
        ? ` Skipped ${postData.skipped_dealers.length} already-paid dealer(s).`
        : "";
      openConfirmNotification(
        `${postData?.message || "Invoices generated successfully."}${skipped}`,
      );
      setReviewOpen(false);
      setSelectedDealerNos([]);
      setCheckedItemNos([]);
      setExpandedDealerNos([]);
      setResultBanner({
        count: postData?.created_dealers?.length || selectedDealerNos.length,
        amount: reviewAmount,
        skipped: postData?.skipped_dealers?.length || 0,
        month: previewParams.Month || filter.Month,
      });
      previewRefetch();
      setProcessView("update");
    }
  }, [postError, postData, postLoading]);

  const onSearch = (value) => {
    console.log("search:", value);
  };

  const buttonsData = [
    {
      label: "List",
      icon: <UnorderedListOutlined className="" />,
      onClick: () => {
        navigate(-1);
      },
    },
  ];
  const breadcrumbItems = [
    {
      label: "Invoice",
      href: "/invoice",
    },
    {
      label: "New Invoice",
      href: `/invoice/add`,
    },
  ];

  const renderDealerTable = (dealers, flagged) => {
    const orderColumns = [
      {
        title: "Order",
        dataIndex: "OrderNo",
        width: 90,
        render: (value) => value || "—",
      },
      {
        title: "Item",
        dataIndex: "Item",
        ellipsis: true,
        render: (value, record) => {
          const kind = orderKindLabel(record);
          return (
            <span className="invoice-dealer-cell">
              <span>{value || "—"}</span>
              {kind ? (
                <span
                  className={`invoice-process-pill ${
                    kind === "Update" ? "is-update" : "is-remaining"
                  }`}
                >
                  {kind}
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        title: "Quantity",
        width: 90,
        render: (_, record) => quantityLabel(record),
      },
      {
        title: "Device",
        dataIndex: "Mac",
        ellipsis: true,
        render: (value, record) =>
          `${value || "—"}${record.LicenseType ? ` · ${record.LicenseType}` : ""}`,
      },
      {
        title: "Period",
        width: 140,
        render: (_, record) => periodLabel(record),
      },
      {
        title: "Amount",
        dataIndex: "Amount",
        width: 120,
        render: (value) => formatMoney(value),
      },
    ];

    const columns = [
      {
        title: "No",
        dataIndex: "DealerNo",
        width: 80,
        render: (value) => value,
      },
      {
        title: "Dealer",
        dataIndex: "DealerName",
        ellipsis: true,
        render: (value, record) => (
          <span className="invoice-dealer-cell">
            <button
              type="button"
              className="invoice-dealer-name"
              onClick={(event) => toggleExpand(record.DealerNo, event)}
            >
              {value || "N/A"}
            </button>
            <span
              className={`invoice-process-pill ${
                flagged
                  ? record.Kind === "update"
                    ? "is-update"
                    : "is-remaining"
                  : "is-new"
              }`}
            >
              {flagged ? remainingKindLabel(record) : "New invoice"}
            </span>
          </span>
        ),
      },
      flagged
        ? {
            title: "Invoice",
            dataIndex: "InvoiceNo",
            width: 110,
            render: (value) => value || "—",
          }
        : {
            title: "OEM",
            dataIndex: "OemName",
            width: 160,
            ellipsis: true,
            render: (value) => value || "N/A",
          },
      {
        title: "Orders",
        dataIndex: "OrderCount",
        width: 150,
        render: (value, record) => {
          if (flagged) {
            const remaining = record.RemainingCount || 0;
            const updates = record.UpdateCount || 0;
            const parts = [];
            if (remaining) parts.push(`${remaining} remaining`);
            if (updates) parts.push(`${updates} update${updates === 1 ? "" : "s"}`);
            if (!parts.length) parts.push(`${value || 0} open`);
            if (record.Age) parts.push(record.Age);
            return parts.join(" · ");
          }
          return `${value || 0}${
            record.PriorCount ? ` · ${record.PriorCount} active` : ""
          }`;
        },
      },
      {
        title: "Amount",
        dataIndex: "Amount",
        width: 130,
        render: (value) => formatMoney(value),
      },
    ];

    return (
      <div className={`my-table invoice-dealer-table ${flagged ? "is-flagged" : ""}`}>
        <Table
          rowKey="DealerNo"
          columns={columns}
          dataSource={dealers}
          pagination={false}
          showSorterTooltip={false}
          rowSelection={{
            selectedRowKeys: selectedDealerNos,
            onChange: (keys) => setSelectedDealerNos(keys),
          }}
          expandable={{
            showExpandColumn: false,
            expandIcon: () => null,
            indentSize: 0,
            expandedRowKeys: expandedDealerNos,
            expandedRowRender: (record) => {
              const orders = record.Orders || [];
              if (!orders.length) {
                return (
                  <div className="text-[12.5px] text-[#8a93a1] px-2 py-1">
                    No new orders. Monthly licenses will be billed for the full
                    month.
                  </div>
                );
              }
              return (
                <Table
                  size="small"
                  rowKey="ItemNo"
                  columns={orderColumns}
                  dataSource={orders}
                  pagination={false}
                />
              );
            },
          }}
        />
      </div>
    );
  };

  return (
    <>
      <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonsData} />
        </CommonBreadcrumb>
      </div>
      <div className="border bg-white rounded-[4px] p-4 overflow-hidden">
        <div className="invoice-steps">
          <div className={`invoice-step ${generateStep === 1 ? "is-current" : "is-done"}`}>
            <span className="invoice-step-num">1</span>
            <span>
              <span className="invoice-step-title">Choose month</span>
              <span className="invoice-step-copy">{monthLabel}</span>
            </span>
          </div>
          <div className="invoice-step-line" />
          <div
            className={`invoice-step ${
              generateStep === 2 ? "is-current" : generateStep > 2 ? "is-done" : ""
            }`}
          >
            <span className="invoice-step-num">2</span>
            <span>
              <span className="invoice-step-title">Select dealers</span>
              <span className="invoice-step-copy">
                {selectedDealerNos.length
                  ? `${selectedDealerNos.length} selected`
                  : "Pick who to bill"}
              </span>
            </span>
          </div>
          <div className="invoice-step-line" />
          <div className={`invoice-step ${generateStep === 3 ? "is-current" : ""}`}>
            <span className="invoice-step-num">3</span>
            <span>
              <span className="invoice-step-title">Review & generate</span>
              <span className="invoice-step-copy">Check lines, then confirm</span>
            </span>
          </div>
        </div>

        <div className="">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={8} xl={8}>
              <Form form={form}>
                <CustomDateTimePicker
                  rules={[{ required: true, message: "Month is required" }]}
                  name="Month"
                  placeholder="Month *"
                  picker="month"
                  onChange={(value) => {
                    if (value) {
                      const formattedMonth = dayjs(value).format("YYYY-MM");
                      const next = { ...filter, Month: formattedMonth };
                      setFilter(next);
                      applyPreview(next);
                    } else {
                      setFilter({ ...filter, Month: "" });
                    }
                  }}
                />
              </Form>
            </Col>
            {![4, 5, 6, 7, 8, 9, 10, 11, 12].includes(logData?.role_value) && (
              <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                <Select
                  showSearch
                  allowClear
                  placeholder="OEM"
                  optionFilterProp="label"
                  value={filter.OemNo || undefined}
                  onChange={(value) => {
                    const next = { ...filter, OemNo: value, DealerNo: "" };
                    setFilter(next);
                    applyPreview(next);
                  }}
                  onSearch={onSearch}
                  options={modifiedOptions.oems}
                  style={{ width: "100%" }}
                />
              </Col>
            )}

            {![7, 8, 9].includes(logData?.role_value) && (
              <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Dealer"
                  optionFilterProp="label"
                  value={filter.DealerNo ? Number(filter.DealerNo) : undefined}
                  onChange={(value) => {
                    const next = { ...filter, DealerNo: value };
                    setFilter(next);
                    applyPreview(next);
                  }}
                  onSearch={onSearch}
                  options={modifiedOptions.dealers}
                  style={{ width: "100%" }}
                />
              </Col>
            )}
          </Row>
        </div>
        <div className="flex justify-between my-4 overflow-x-auto !items-center">
          <div className="flex items-center">
            <Button
              type="primary"
              className="mr-2 w-[104px] h-[30px] font-medium"
              onClick={handleFilter}
              disabled={!filter.Month}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>
            <Button
              className="reset-button w-[104px] h-[30px] font-medium"
              onClick={handleReset}
            >
              <HiArrowPath className="!text-[14px] font-medium" />
              Reset
            </Button>
            {[1, 2, 3].includes(role_value) && (
              <Button
                type="primary"
                className="ml-2 h-[30px] font-medium"
                disabled={selectedDealerNos.length === 0}
                onClick={openReview}
              >
                <PlusOutlined />
                Generate invoices
              </Button>
            )}
          </div>
        </div>

        {resultBanner ? (
          <div className="invoice-result-banner">
            <div>
              <div className="invoice-result-title">
                Generated {resultBanner.count} invoice
                {resultBanner.count === 1 ? "" : "s"}
              </div>
              <div className="invoice-result-sub">
                {formatMoney(resultBanner.amount)}
                {resultBanner.month
                  ? ` · ${formatYearMonthToLabel(resultBanner.month)}`
                  : ""}
                {resultBanner.skipped
                  ? ` · skipped ${resultBanner.skipped} already-paid dealer(s)`
                  : ""}
              </div>
            </div>
            <Button type="primary" onClick={() => navigate("/invoice")}>
              View invoices
            </Button>
          </div>
        ) : null}

        {previewLoading ? (
          <div className="py-10 text-center text-[#8a93a1]">
            Loading dealers…
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="invoice-process-grid">
              <button
                type="button"
                className={`invoice-process-card ${
                  processView === "open" ? "is-active" : ""
                }`}
                onClick={() => setProcessView("open")}
              >
                <div className="invoice-process-kicker">Process 1</div>
                <div className="invoice-process-title">New invoices</div>
                <div className="invoice-process-desc">
                  Dealers not billed for {monthLabel} yet. Same monthly
                  calculation as before.
                </div>
                <div className="invoice-process-stat">
                  {openDealers.length} dealer
                  {openDealers.length === 1 ? "" : "s"}
                  {previewSummary.OpenAmount
                    ? ` · ${formatMoney(previewSummary.OpenAmount)}`
                    : ""}
                  {selectedOpenCount ? ` · ${selectedOpenCount} selected` : ""}
                </div>
              </button>
              <button
                type="button"
                className={`invoice-process-card is-update ${
                  processView === "update" ? "is-active" : ""
                }`}
                onClick={() => setProcessView("update")}
              >
                <div className="invoice-process-kicker">Process 2</div>
                <div className="invoice-process-title">Still to invoice</div>
                <div className="invoice-process-desc">
                  Already invoiced this month, but leftover features or later
                  device changes are still open.
                </div>
                <div className="invoice-process-stat">
                  {flaggedDealers.length} dealer
                  {flaggedDealers.length === 1 ? "" : "s"}
                  {previewSummary.FlaggedAmount
                    ? ` · ${formatMoney(previewSummary.FlaggedAmount)}`
                    : ""}
                  {selectedUpdateCount
                    ? ` · ${selectedUpdateCount} selected`
                    : ""}
                </div>
              </button>
            </div>

            {processView === "update" ? (
              <div>
                <div className="invoice-section-label">
                  Still to invoice · {flaggedDealers.length} dealer
                  {flaggedDealers.length === 1 ? "" : "s"}
                  {previewSummary.FlaggedAmount
                    ? ` · ${formatMoney(previewSummary.FlaggedAmount)}`
                    : ""}
                </div>
                {flaggedDealers.length ? (
                  <div className="invoice-flagbox">
                    {renderDealerTable(flaggedDealers, true)}
                  </div>
                ) : (
                  <div className="text-[13px] text-[#8a93a1] border border-dashed border-gray-200 rounded-[10px] px-4 py-3">
                    Nothing left to invoice for dealers already billed this month.
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="invoice-section-label">
                  New invoices · {openDealers.length} dealer
                  {openDealers.length === 1 ? "" : "s"}
                  {previewSummary.OpenAmount
                    ? ` · ${formatMoney(previewSummary.OpenAmount)}`
                    : ""}
                </div>
                <div className="invoice-openbox">
                  {openDealers.length ? (
                    renderDealerTable(openDealers, false)
                  ) : (
                    <div className="px-4 py-6 text-[13px] text-[#8a93a1]">
                      No dealers to invoice for {monthLabel}.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={reviewOpen}
        onCancel={() => setReviewOpen(false)}
        closable={false}
        footer={null}
        width={1080}
        centered
        wrapClassName="invoice-review-modal"
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <PlusOutlined className="text-[15px] text-black ml-1" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Generate invoices
            </span>
          </div>
        }
      >
        <div>
          <div className="bg-white rounded-[4px] overflow-hidden">
            <div className="px-2 pt-2 pb-3">
              <p className="text-[13px] text-[#576273] mb-3">
                {selectedDealerNos.length} dealer
                {selectedDealerNos.length === 1 ? "" : "s"}
                {selectedOpenCount ? ` · ${selectedOpenCount} new` : ""}
                {selectedUpdateCount ? ` · ${selectedUpdateCount} update` : ""}
                {" · "}
                {formatMoney(reviewAmount)}
              </p>
              <div className="invoice-openbox">
                <div className="my-table invoice-dealer-table invoice-review-table">
                  <Table
                    rowKey="rowKey"
                    columns={reviewColumns}
                    dataSource={reviewTableRows}
                    pagination={false}
                    showSorterTooltip={false}
                    tableLayout="fixed"
                    rowSelection={{
                      columnWidth: 48,
                      selectedRowKeys: reviewTableRows
                        .filter((order) =>
                          checkedItemNos.includes(Number(order.ItemNo)),
                        )
                        .map((order) => order.rowKey),
                      onChange: (_keys, rows) => {
                        setCheckedItemNos(
                          rows
                            .map((row) => Number(row.ItemNo))
                            .filter((id) => Number.isInteger(id) && id > 0),
                        );
                      },
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-center space-x-2 mt-5">
                <Button
                  type="primary"
                  className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
                  loading={postLoading}
                  disabled={selectedDealerNos.length === 0}
                  onClick={confirmGenerate}
                >
                  <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                  <span>Apply</span>
                </Button>
                <Button
                  className="reset-button"
                  onClick={() => setReviewOpen(false)}
                >
                  <CloseOutlined />
                  <span>Cancel</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default InvoiceCreateForm;
